import * as React from "react";
import {
  AlertCircle,
  AlertTriangle,
  ClipboardEdit,
  Info,
  Activity,
  OctagonAlert,
} from "lucide-react";
import { Pill } from "@/components/ui/pill";
import type { MachineEventSummary } from "@/features/lines/hooks";
import { MachineLabel } from "../MachineLabel";
import { fmtTs, type UnifiedItem } from "./types";

const EVENT_META: Record<
  MachineEventSummary["type"],
  {
    icon: React.ComponentType<{ className?: string }>;
    tone: "danger" | "warning" | "info" | "neutral" | "success";
    label: string;
  }
> = {
  STOP: { icon: OctagonAlert, tone: "danger", label: "Parada" },
  START: { icon: Activity, tone: "success", label: "Início" },
  ALERT: { icon: AlertTriangle, tone: "warning", label: "Alerta" },
  MAINTENANCE: { icon: Info, tone: "info", label: "Manutenção" },
  NODATA: { icon: AlertCircle, tone: "neutral", label: "Sem dados" },
};

interface EventRowProps {
  item: UnifiedItem;
  onSelect?: (item: UnifiedItem) => void;
}

export function EventRow({ item, onSelect }: EventRowProps) {
  const clickable = !!onSelect;
  const handleClick = () => onSelect?.(item);

  if (item.kind === "event") {
    const meta = EVENT_META[item.event.type] ?? EVENT_META.NODATA;
    const Icon = meta.icon;
    return (
      <li data-testid={`event-row-${item.event.id}`}>
        <RowShell clickable={clickable} onClick={handleClick}>
          <Icon className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden="true" />
          <span className="shrink-0 font-mono text-[12px] tabular-nums text-text-tertiary">
            {fmtTs(item._at)}
          </span>
          <Pill tone={meta.tone} withDot>
            {meta.label}
          </Pill>
          <span className="flex-1 truncate text-[13px] text-text-secondary">
            {item.event.reason ? (
              <>
                {item.event.reason}
                {item.event.machineName ? (
                  <span className="ml-1.5 text-text-tertiary">· {item.event.machineName}</span>
                ) : null}
              </>
            ) : (
              // Review item 6 — STOP/START/NODATA não têm motivo; mostrar a máquina de origem.
              <MachineLabel name={item.event.machineName} code={item.event.machineCode} />
            )}
          </span>
          {item.event.durationMinutes !== null ? (
            <span className="font-mono text-[12px] tabular-nums text-text-tertiary">
              {item.event.durationMinutes < 60
                ? `${item.event.durationMinutes}m`
                : `${(item.event.durationMinutes / 60).toFixed(1)}h`}
            </span>
          ) : null}
        </RowShell>
      </li>
    );
  }
  return (
    <li data-testid={`manual-entry-row-${item.entry.id}`}>
      <RowShell clickable={clickable} onClick={handleClick}>
        <ClipboardEdit className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden="true" />
        <span className="shrink-0 font-mono text-[12px] tabular-nums text-text-tertiary">
          {fmtTs(item._at)}
        </span>
        <Pill tone="info" withDot>
          Entrada manual
        </Pill>
        <span className="flex-1 truncate text-[13px] text-text-secondary">
          {item.entry.totalRows ?? 1} lançamento(s)
        </span>
        <span className="font-mono text-[11px] tabular-nums text-text-tertiary">
          {item.entry.status}
        </span>
      </RowShell>
    </li>
  );
}

function RowShell({
  clickable,
  onClick,
  children,
}: {
  clickable: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  if (!clickable) {
    return <div className="flex items-center gap-3 px-1 py-2.5">{children}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="hover:bg-bg-muted-rd/40 flex w-full items-center gap-3 rounded px-1 py-2.5 text-left focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]"
    >
      {children}
    </button>
  );
}
